import * as React from 'react';

import { Flex, Box } from '@lightspeed/flame/Core';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import { Input } from '@lightspeed/flame/Input';
import { InputGroup, InputGroupAddon } from '@lightspeed/flame/InputGroup';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import Localize from 'app/src/localize/Localize';
import SaleManager from 'app/src/payments/SaleManager';

interface Props {
  onHide: () => void;
  onFinishSale: (amount: Decimal) => Promise<void>;
  saleManager: SaleManager;
}

interface DenominationsProps {
  onClick: (value: Decimal) => void;
  isLoading: boolean;
}

const inputMaxLength: number = 14; // 99 999 999 999.99 Should be enough

/**
 * Renders the denominations.  The denominations are pulled from the current
 * locale settings.
 */
const RenderDenominations: React.FC<DenominationsProps> = ({ onClick, isLoading }) => {
  return (
    <>
      {Localize.getLocalizedDenominations().map(denomination => {
        return (
          <Box p={1} key={denomination.get('value')}>
            <Button
              value={Format.parseDecimal(denomination.get('value'), new Decimal(0))}
              onClick={() => {
                onClick(denomination.get('value'));
              }}
              size="large"
              role="button"
              disabled={isLoading}
            >
              {denomination.get('name')}
            </Button>
          </Box>
        );
      })}
    </>
  );
};

const QuickPayCashModal: React.FC<Props> = ({ onHide, onFinishSale, saleManager }) => {
  const [totalDue, setTotalDue] = React.useState<Decimal>(saleManager.saleTotal);
  const [balance, setBalance] = React.useState<Decimal>(saleManager.pendingBalance);
  const [amount, setAmount] = React.useState<Decimal>(new Decimal(0));
  const [change, setChange] = React.useState<Decimal>(new Decimal(0));
  const [inputAmount, setInputAmount] = React.useState<string>(Format.formatMoney(0, 0, false));
  const [isLoading, setIsLoading] = React.useState<boolean>(saleManager.isSaleUpdating || false);
  const [isOpen, setIsOpen] = React.useState<boolean>(true);
  const [isFinishingSale, setIsFinishingSale] = React.useState<boolean>(false);
  const [isFocus, setIsFocus] = React.useState<boolean>(false);
  const [hasOutstandingBalance, setHasOutstandingBalance] = React.useState<boolean>(false);

  const handleSaleRefreshing = () => {
    setIsLoading(true);
  };

  const handleSaleRefreshed = () => {
    setTotalDue(saleManager.saleTotal);
    setBalance(saleManager.pendingBalance);
    setIsLoading(false);
  };

  // ComponentDidMount
  React.useEffect(() => {
    window.addEventListener(SaleManager.Events.SALE_REFRESHING, handleSaleRefreshing);
    window.addEventListener(SaleManager.Events.SALE_REFRESHED, handleSaleRefreshed);
    // ComponentWillUnmount
    return () => {
      window.removeEventListener(SaleManager.Events.SALE_REFRESHING, handleSaleRefreshing);
      window.removeEventListener(SaleManager.Events.SALE_REFRESHED, handleSaleRefreshed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ComponentDidUpdate
  React.useEffect(() => {
    if (amount instanceof Decimal) {
      const currentBalance: Decimal = totalDue.minus(amount);

      if (currentBalance.isLessThanOrEqualTo(0)) {
        setChange(currentBalance);
        setBalance(new Decimal(0));
        setHasOutstandingBalance(false);
      } else {
        setChange(new Decimal(0));
        setBalance(currentBalance);
        setHasOutstandingBalance(true);
      }
    }
  }, [amount, totalDue, saleManager.isSaleUpdating]);

  // Handlers - Amount Input
  const handleOnChangeAmount = (event: TSFixMe) => {
    const sanitizedInputValue = event.target.value.replace(/[^\d.]/, '');
    const allowInputPattern = new RegExp(/^\d{0,11}(.?\d{0,2})?$/);

    if (sanitizedInputValue.match(allowInputPattern) || sanitizedInputValue === '') {
      setInputAmount(sanitizedInputValue);
    }
  };

  const handleOnFocusAmount = (event: TSFixMe) => {
    setIsFocus(true);
    setInputAmount(event.target.value);
    event.target.select();
  };

  const handleOnBlurAmount = () => {
    setIsFocus(false);

    const maximumLengthWithDecimals = inputMaxLength - (Localize.getCurrencyPrecision() + 1);

    // The following code will try to fit a decimal into the inputMaxLength by transforming
    //  he last digits based on the the getCurrencyPrecision into a decimal value
    // starting with a dot.
    const trimmedAmount =
      inputAmount.length > maximumLengthWithDecimals
        ? `${inputAmount.substr(0, maximumLengthWithDecimals)}.${inputAmount.substr(
            maximumLengthWithDecimals + 1,
            Localize.getCurrencyPrecision()
          )}`
        : inputAmount;
    setAmount(Format.parseDecimal(trimmedAmount, new Decimal(0)));
  };

  const handleOnKeyPressAmount = (event: TSFixMe) => {
    // prevent the global keypress handler from firing
    event.stopPropagation();
    if (event.key === 'Enter') {
      // blurring the input will trigger the handleAmountBlur method
      event.currentTarget.blur();
    }
  };

  // Handlers - Denomination Button
  const handleOnClickDenomination = (value: TSFixMe) => {
    setAmount(amount.plus(value));
  };

  // Handlers - Finish Sale Button
  const handleOnClickOnFinishSale = () => {
    setIsFinishingSale(true);
    onFinishSale(amount)
      .then(() => {
        setIsOpen(false);
        setIsFinishingSale(false);
      })
      .catch(() => {
        // We let Retail Legacy UI dealt with any problem coming back from this call.
        setIsOpen(false);
        setIsFinishingSale(false);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
        onHide();
      }}
    >
      <ModalHeader
        showCloseButton={false}
        onCloseClicked={() => {
          setIsOpen(false);
          onHide();
        }}
        role="heading"
      >
        Cash
      </ModalHeader>
      <ModalBody>
        <Box width="402px">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection="row"
            flexWrap="wrap"
            flexBasis="25%"
          >
            <RenderDenominations
              isLoading={isLoading || isFinishingSale}
              onClick={handleOnClickDenomination}
            />
          </Flex>
          <Box mt={2}>
            <label htmlFor="quickPayAmountId">
              <Text size="small" fontWeight="bold" mb={1}>
                Amount
              </Text>
            </label>
            <InputGroup>
              <InputGroupAddon>{Localize.getCurrencySymbol()}</InputGroupAddon>
              <Input
                id="quickPayAmountId"
                value={isFocus ? inputAmount : Format.formatMoney(amount, 0, false)}
                onChange={handleOnChangeAmount}
                onFocus={handleOnFocusAmount}
                onBlur={handleOnBlurAmount}
                onKeyPress={handleOnKeyPressAmount}
                disabled={isLoading || isFinishingSale}
                autoFocus
                autoComplete="off"
                maxLength={inputMaxLength}
              />
              <Button
                id="quickPayAmountMax"
                onClick={() => {
                  setAmount(totalDue);
                }}
                disabled={isLoading || isFinishingSale}
                role="button"
              >
                MAX
              </Button>
            </InputGroup>
          </Box>
          <Divider mt={4} mb={4} />
          <Flex justifyContent="space-between" mb={2}>
            <Text size="small" fontWeight="bold">
              Total Due
            </Text>
            {isLoading ? (
              <Bone width="50%" height={3} />
            ) : (
              <Text size="small">{Format.formatMoney(totalDue, 0)}</Text>
            )}
          </Flex>
          <Flex justifyContent="space-between" mb={2}>
            <Text size="small" fontWeight="bold">
              Balance
            </Text>
            {isLoading ? (
              <Bone width="50%" height={3} />
            ) : (
              <Text size="small" color={hasOutstandingBalance ? 'maple-500' : 'green-700'}>
                {Format.formatMoney(balance, 0)}
              </Text>
            )}
          </Flex>
          <Flex justifyContent="space-between">
            <Text size="small" fontWeight="bold">
              Change
            </Text>
            {isLoading ? (
              <Bone width="50%" height={3} />
            ) : (
              <Text size="small">{Format.formatMoney(change, 0)}</Text>
            )}
          </Flex>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Flex justifyContent="flex-end">
          <Button
            mr="2"
            onClick={() => {
              setIsOpen(false);
            }}
            role="button"
            disabled={isFinishingSale}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            fill
            onClick={handleOnClickOnFinishSale}
            disabled={isLoading || hasOutstandingBalance || isFinishingSale}
            loading={isFinishingSale}
            role="button"
          >
            Finish Sale
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export { QuickPayCashModal };
